import React, { useState, useEffect, useCallback, useRef } from "react";
import ioClient from "socket.io-client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  QuestionAnswer as QAIcon,
  EmojiEvents as TrophyIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const getToken = () => localStorage.getItem("admin_token");

const BeerillionerAudienceModal = ({ open, onClose, sessionId }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const socketRef = useRef(null);

  const fetchResults = useCallback(async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/beerillioner/sessions/${sessionId}/audience-results`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed to fetch");
      const json = await res.json();
      if (json.success) setData(json);
    } catch (err) {
      console.error("[BeerillionerAudienceModal] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!open || !sessionId) return;
    fetchResults();
  }, [open, sessionId, fetchResults]);

  useEffect(() => {
    if (!open || !sessionId) return;

    const socket = ioClient(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("beer:join-global");
    });

    const refresh = () => fetchResults();

    socket.on("beer:audience_play_update", refresh);
    socket.on("beer:audience_answers_locked", refresh);
    socket.on("beer:audience_play_started", refresh);
    socket.on("beer:answer_revealed", refresh);
    socket.on("beer:next_question", refresh);
    socket.on("beer:question_pushed", refresh);

    const interval = setInterval(refresh, 4000);

    return () => {
      clearInterval(interval);
      if (socketRef.current) {
        socketRef.current.emit("beer:leave-global");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [open, sessionId, fetchResults]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { bgcolor: "#0f0f0f", color: "#fff", borderRadius: 3, minHeight: "70vh" },
      }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #222", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssessmentIcon sx={{ color: "#e040fb" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Audience Results
          </Typography>
          {data && (
            <Chip
              label={`Level ${data.current_level}`}
              size="small"
              sx={{ bgcolor: "rgba(224,64,251,0.15)", color: "#f3e5f5", border: "1px solid #e040fb" }}
            />
          )}
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: "1px solid #222" }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{
            px: 2,
            "& .MuiTab-root": { color: "#888", fontWeight: 600, textTransform: "none", fontSize: "0.95rem" },
            "& .Mui-selected": { color: "#f3e5f5" },
            "& .MuiTabs-indicator": { backgroundColor: "#e040fb" },
          }}
        >
          <Tab label="Current" icon={<QAIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Users" icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        {loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#e040fb" }} />
          </Box>
        ) : !data ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography sx={{ color: "#666" }}>No data available</Typography>
          </Box>
        ) : (
          <>
            {tabIndex === 0 && <CurrentTab data={data} />}
            {tabIndex === 1 && <UsersTab data={data} />}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #222", p: 2 }}>
        <Button onClick={onClose} sx={{ color: "#aaa" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ============================= CURRENT TAB ============================= */
const OPTION_COLORS = { A: "#ef5350", B: "#42a5f5", C: "#ffa726", D: "#66bb6a" };

const CurrentTab = ({ data }) => {
  const {
    current_level,
    current_question,
    correct_answer,
    connected_count,
    answered_count,
    not_answered_count,
    current_level_stats,
    answered,
    not_answered_players,
    audience_play_active,
    audience_answers_locked,
  } = data;

  const correctCount = correct_answer ? answered.filter(p => p.is_correct).length : 0;
  const wrongCount = correct_answer ? answered.filter(p => p.is_correct === false).length : 0;

  const pct = connected_count > 0 ? Math.round((answered_count / connected_count) * 100) : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Current question info */}
      {current_question && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(224,64,251,0.06)",
            border: "1px solid rgba(224,64,251,0.25)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <QAIcon sx={{ color: "#e040fb", fontSize: 22 }} />
            <Typography variant="caption" sx={{ color: "#aaa" }}>
              Level {current_level} Question
            </Typography>
            {audience_play_active && (
              <Chip
                label="LIVE"
                size="small"
                sx={{
                  ml: "auto",
                  bgcolor: "#4caf50",
                  color: "#fff",
                  fontWeight: 700,
                  animation: "pulse 1.5s infinite",
                }}
              />
            )}
            {audience_answers_locked && (
              <Chip
                label="LOCKED"
                size="small"
                sx={{ ml: "auto", bgcolor: "#f44336", color: "#fff", fontWeight: 700 }}
              />
            )}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#fff" }}>
            {current_question.question_text || current_question.text || "Question active"}
          </Typography>
        </Box>
      )}

      {!current_question && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ color: "#888" }}>No question is currently active</Typography>
          <Typography variant="caption" sx={{ color: "#555" }}>
            Push a question to start audience play
          </Typography>
        </Box>
      )}

      {/* Answer Progress */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#ccc" }}>
            Answered
          </Typography>
          <Typography variant="body2" sx={{ color: "#e040fb", fontWeight: 700 }}>
            {answered_count} / {connected_count}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "relative",
            height: 32,
            borderRadius: 2,
            bgcolor: "#1a1a1a",
            overflow: "hidden",
            border: "1px solid #333",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${pct}%`,
              background: "linear-gradient(90deg, #9C27B0, #e040fb)",
              borderRadius: 2,
              transition: "width 0.6s ease",
              minWidth: pct > 0 ? 30 : 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              fontWeight: 700,
              color: "#fff",
              textShadow: "0 1px 3px rgba(0,0,0,0.5)",
            }}
          >
            {pct}%
          </Typography>
        </Box>

        {/* Stat chips */}
        <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
          {correct_answer && (
            <Box
              sx={{
                flex: 1,
                minWidth: 80,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "rgba(76,175,80,0.1)",
                border: "1px solid rgba(76,175,80,0.3)",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#4caf50" }}>
                {correctCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#81c784" }}>
                Correct
              </Typography>
            </Box>
          )}
          {correct_answer && (
            <Box
              sx={{
                flex: 1,
                minWidth: 80,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "rgba(244,67,54,0.1)",
                border: "1px solid rgba(244,67,54,0.3)",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#f44336" }}>
                {wrongCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#e57373" }}>
                Wrong
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 80,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "rgba(224,64,251,0.1)",
              border: "1px solid rgba(224,64,251,0.3)",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#e040fb" }}>
              {answered_count}
            </Typography>
            <Typography variant="caption" sx={{ color: "#ce93d8" }}>
              Answered
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 80,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "rgba(255,152,0,0.1)",
              border: "1px solid rgba(255,152,0,0.3)",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#ff9800" }}>
              {not_answered_count}
            </Typography>
            <Typography variant="caption" sx={{ color: "#ffb74d" }}>
              Not Answered
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 80,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "rgba(33,150,243,0.1)",
              border: "1px solid rgba(33,150,243,0.3)",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#2196f3" }}>
              {connected_count}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64b5f6" }}>
              Connected
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Option breakdown bar chart */}
      {current_level_stats && current_level_stats.total_answers > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ color: "#ccc", mb: 1.5 }}>
            Answer Distribution
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {["A", "B", "C", "D"].map((opt) => {
              const pctVal = current_level_stats.percentages?.[opt] || 0;
              return (
                <Box key={opt} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${OPTION_COLORS[opt]}22`,
                      color: OPTION_COLORS[opt],
                      fontWeight: 800,
                      fontSize: "0.85rem",
                      border: `1px solid ${OPTION_COLORS[opt]}55`,
                      flexShrink: 0,
                    }}
                  >
                    {opt}
                  </Box>
                  <Box sx={{ flex: 1, position: "relative", height: 24, borderRadius: 1, bgcolor: "#1a1a1a", overflow: "hidden" }}>
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${pctVal}%`,
                        bgcolor: OPTION_COLORS[opt],
                        opacity: 0.7,
                        borderRadius: 1,
                        transition: "width 0.5s ease",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: OPTION_COLORS[opt], fontWeight: 700, minWidth: 40, textAlign: "right" }}
                  >
                    {pctVal}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Not answered info with names */}
      {not_answered_count > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "#ff9800", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            <CancelIcon sx={{ fontSize: 18 }} />
            {not_answered_count} player{not_answered_count !== 1 ? "s" : ""} haven't answered yet
          </Typography>
          {not_answered_players && not_answered_players.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {not_answered_players.map((p, i) => (
                <Chip
                  key={p.device_id || i}
                  label={p.name}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,152,0,0.1)",
                    color: "#ffb74d",
                    border: "1px solid rgba(255,152,0,0.3)",
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Answered list */}
      {answered && answered.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "#e040fb", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            <CheckCircleIcon sx={{ fontSize: 18 }} /> Answered Players
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: "#121212", borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#999", fontWeight: 600, borderBottom: "1px solid #222" }}>
                    Name
                  </TableCell>
                  <TableCell align="center" sx={{ color: "#999", fontWeight: 600, borderBottom: "1px solid #222" }}>
                    Answer
                  </TableCell>
                  {correct_answer && (
                    <TableCell align="center" sx={{ color: "#999", fontWeight: 600, borderBottom: "1px solid #222" }}>
                      Result
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {answered.map((p, i) => (
                  <TableRow key={p.device_id || i} sx={{ "&:hover": { bgcolor: "#1a1a1a" } }}>
                    <TableCell sx={{ color: "#fff", borderBottom: "1px solid #1a1a1a" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {correct_answer ? (
                          p.is_correct ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 16, color: "#f44336" }} />
                          )
                        ) : (
                          <CheckCircleIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                        )}
                        {p.name}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: "1px solid #1a1a1a" }}>
                      <Chip
                        label={p.answer}
                        size="small"
                        sx={{
                          bgcolor: `${OPTION_COLORS[p.answer] || "#888"}22`,
                          color: OPTION_COLORS[p.answer] || "#fff",
                          fontWeight: 700,
                          border: `1px solid ${OPTION_COLORS[p.answer] || "#555"}55`,
                          minWidth: 36,
                        }}
                      />
                    </TableCell>
                    {correct_answer && (
                      <TableCell align="center" sx={{ borderBottom: "1px solid #1a1a1a" }}>
                        <Chip
                          label={p.is_correct ? "Correct" : "Wrong"}
                          size="small"
                          sx={{
                            bgcolor: p.is_correct ? "rgba(76,175,80,0.15)" : "rgba(244,67,54,0.15)",
                            color: p.is_correct ? "#4caf50" : "#f44336",
                            fontWeight: 700,
                            border: `1px solid ${p.is_correct ? "rgba(76,175,80,0.4)" : "rgba(244,67,54,0.4)"}`,
                          }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

/* ============================= USERS TAB ============================= */
const UsersTab = ({ data }) => {
  const { users } = data;

  if (!users || users.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <PeopleIcon sx={{ fontSize: 56, color: "#444", mb: 2 }} />
        <Typography sx={{ color: "#888" }}>No audience data yet</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {users.map((user, idx) => (
        <Accordion
          key={user.device_id}
          sx={{
            bgcolor: "#121212",
            border: "1px solid #222",
            borderRadius: "10px !important",
            mb: 1.5,
            "&:before": { display: "none" },
            "&.Mui-expanded": { borderColor: "#e040fb", mb: 1.5 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#888" }} />}
            sx={{
              borderRadius: "10px",
              "&:hover": { bgcolor: "rgba(224,64,251,0.04)" },
              "& .MuiAccordionSummary-content": { alignItems: "center", my: 1 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1.5 }}>
              {/* Rank badge */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    idx === 0
                      ? "rgba(255,215,0,0.15)"
                      : idx === 1
                        ? "rgba(192,192,192,0.15)"
                        : idx === 2
                          ? "rgba(205,127,50,0.15)"
                          : "#1a1a1a",
                  color:
                    idx === 0 ? "#FFD700" : idx === 1 ? "#C0C0C0" : idx === 2 ? "#CD7F32" : "#888",
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  border: `1px solid ${
                    idx === 0
                      ? "rgba(255,215,0,0.3)"
                      : idx === 1
                        ? "rgba(192,192,192,0.3)"
                        : idx === 2
                          ? "rgba(205,127,50,0.3)"
                          : "#333"
                  }`,
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#e5e7eb" }} noWrap>
                  {user.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "#777" }}>
                  {user.total_answered} answer{user.total_answered !== 1 ? "s" : ""}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
                <Chip
                  icon={<TrophyIcon sx={{ fontSize: 14 }} />}
                  label={`${user.total_correct} correct`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(76,175,80,0.12)",
                    color: "#81c784",
                    border: "1px solid rgba(76,175,80,0.3)",
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: "#81c784" },
                  }}
                />
                {user.total_answered > 0 && (
                  <Chip
                    label={`${Math.round((user.total_correct / user.total_answered) * 100)}%`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(224,64,251,0.12)",
                      color: "#f3e5f5",
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ bgcolor: "rgba(5,5,5,0.5)", px: 3, py: 2 }}>
            {user.answers.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#666", textAlign: "center", py: 2 }}>
                No answers recorded
              </Typography>
            ) : (
              <Box>
                {/* Summary */}
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#1a1a1a", flex: 1, textAlign: "center" }}>
                    <Typography variant="h6" sx={{ color: "#81c784", fontWeight: 700 }}>
                      {user.total_correct}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      Correct
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#1a1a1a", flex: 1, textAlign: "center" }}>
                    <Typography variant="h6" sx={{ color: "#64b5f6", fontWeight: 700 }}>
                      {user.total_answered}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      Total
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#1a1a1a", flex: 1, textAlign: "center" }}>
                    <Typography variant="h6" sx={{ color: "#e040fb", fontWeight: 700 }}>
                      {user.total_answered > 0
                        ? Math.round((user.total_correct / user.total_answered) * 100)
                        : 0}
                      %
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      Accuracy
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: "#222", mb: 1.5 }} />

                <TableContainer component={Paper} sx={{ bgcolor: "transparent", boxShadow: "none" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#777", fontWeight: 600, borderBottom: "1px solid #222", pl: 0 }}>
                          Level
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "#777", fontWeight: 600, borderBottom: "1px solid #222" }}
                        >
                          Answer
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "#777", fontWeight: 600, borderBottom: "1px solid #222" }}
                        >
                          Correct
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "#777", fontWeight: 600, borderBottom: "1px solid #222", pr: 0 }}
                        >
                          Result
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {user.answers.map((a, ai) => (
                        <TableRow
                          key={ai}
                          sx={{ "&:hover": { bgcolor: "rgba(224,64,251,0.04)" } }}
                        >
                          <TableCell sx={{ borderBottom: "1px solid #1a1a1a", pl: 0 }}>
                            <Typography variant="body2" sx={{ color: "#e5e7eb", fontWeight: 600 }}>
                              Level {a.level}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: "1px solid #1a1a1a" }}>
                            <Chip
                              label={a.answer}
                              size="small"
                              sx={{
                                bgcolor: `${OPTION_COLORS[a.answer] || "#888"}22`,
                                color: OPTION_COLORS[a.answer] || "#fff",
                                fontWeight: 700,
                                minWidth: 36,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: "1px solid #1a1a1a" }}>
                            {a.correct_answer ? (
                              <Chip
                                label={a.correct_answer}
                                size="small"
                                sx={{
                                  bgcolor: `${OPTION_COLORS[a.correct_answer] || "#888"}22`,
                                  color: OPTION_COLORS[a.correct_answer] || "#fff",
                                  fontWeight: 700,
                                  minWidth: 36,
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: "#555" }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: "1px solid #1a1a1a", pr: 0 }}>
                            {a.is_correct === true && (
                              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                            )}
                            {a.is_correct === false && (
                              <CancelIcon sx={{ color: "#f44336", fontSize: 20 }} />
                            )}
                            {a.is_correct === null && (
                              <Typography variant="body2" sx={{ color: "#555" }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default BeerillionerAudienceModal;
